import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HRIS_API_SERVICE, HRIS_QUERY_KEYS } from '@/api/hris';
import { findOptionByName } from '@/components/ats/hris/optionNameMatch';
import { positionReferenceOptionsQuery } from './api';

const JOB_PROFILE_REFERENCE_TYPE = 'Job_Profile_ID';

// A job's linked profile does not change while a recruiter works, and the same
// key is already used by the employee form — so both share one fetch.
const ATS_JOB_STALE_TIME = 5 * 60 * 1000;

/**
 * A type alias, not an interface: only aliases get the implicit index signature
 * that `findOptionByName`'s `OptionEntry` parameter requires.
 */
type MatchableOption = { id: string; name: string };

interface UseJobProfileDefaultArgs {
  /** Kula job the application belongs to. */
  jobId?: string | number;
  /** Profile name when the application already carries it; skips the job fetch. */
  jobProfileName?: string | null;
  enabled?: boolean;
  /** Skips the job fetch when the caller already supplied a title. */
  hasJobTitle?: boolean;
}

/**
 * Resolves the Job profile the position should default to, by matching the
 * Kula job's linked profile name against Workday's job profile list.
 *
 * Same idea as the employee form's job-profile prefill, and deliberately the
 * same matcher (`findOptionByName`): it strips the grade qualifier Workday
 * appends (`Senior Data Engineer (G10)`) and returns nothing when several
 * profiles tie — a blank field costs one click, a wrong profile can be
 * submitted unnoticed.
 */
export const useJobProfileDefault = ({
  jobId,
  jobProfileName,
  enabled = true,
  hasJobTitle = true,
}: UseJobProfileDefaultArgs): {
  jobProfile: MatchableOption | undefined;
  /** The job's own title, for hosts whose application payload omits it. */
  jobName: string | undefined;
} => {
  // The embedded `ats_job` is a trimmed serialization with no `job_profile`, so
  // the name normally has to come from the job itself.
  const { data: job } = useQuery({
    queryKey: HRIS_QUERY_KEYS.atsJob(jobId ?? ''),
    queryFn: () => HRIS_API_SERVICE.getAtsJob(jobId as string | number),
    // Also fetched when only the title is missing: a candidates-table row
    // carries no `ats_job`, so the drawer has nothing to prefill from.
    enabled: enabled && !!jobId && (!jobProfileName || !hasJobTitle),
    staleTime: ATS_JOB_STALE_TIME,
    retry: false,
  });

  const name =
    jobProfileName || job?.job_profile?.name || job?.job_profile?.job_title;

  // Same key the selects' own `loadOptions` uses, so whichever asks first
  // fetches and the other reads that entry — no second copy of the list.
  const { data: response } = useQuery({
    ...positionReferenceOptionsQuery(JOB_PROFILE_REFERENCE_TYPE),
    enabled: enabled && !!name,
    retry: false,
  });

  // Memoised on the cached response rather than derived in `select`: an inline
  // `select` re-runs every render, and a fresh object each time rebuilds the
  // form config that consumes it.
  const jobProfile = useMemo(() => {
    if (!name) return undefined;
    // Narrowed to what the matcher reads.
    const options: MatchableOption[] = (response?.data?.results || []).map(
      ({ id, name: label }) => ({ id, name: label })
    );
    const match = findOptionByName(options, name);
    // The whole option, not just the id: AsyncSelectField resolves a value's
    // label from its `options` list alone, so an id on its own renders blank.
    return match?.id
      ? { id: match.id, name: String(match.name ?? '') }
      : undefined;
  }, [response, name]);

  return { jobProfile, jobName: job?.name };
};
